import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import axiosInstance from '../config/axios.config';


export default function EmploymentType() {
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newTypeDialog, setNewTypeDialog] = useState(false);
    const [editTypeDialog, setEditTypeDialog] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [newType, setNewType] = useState({ employment_type: '', description: '' });
    const toast = useRef(null);

    // ✅ Fetch all employment types
    const loadEmploymentTypes = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/employment/types');
            console.log('Employment API Response:', response.data);
            if (response.data.success) {
                const dataToSet = response.data.data;
                // Handle nested data structure
                if (Array.isArray(dataToSet)) {
                    setEmploymentTypes(dataToSet);
                } else if (dataToSet && Array.isArray(dataToSet.data)) {
                    setEmploymentTypes(dataToSet.data);
                } else {
                    setEmploymentTypes([]);
                }
            } else {
                setEmploymentTypes([]);
            }
        } catch (error) {
            console.error('Error loading employment types:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load employment types',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmploymentTypes();
    }, []);

    // ✅ Create new employment type
    const createEmploymentType = async () => {
        if (!newType.employment_type.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Type name is required',
                life: 3000
            });
            return;
        }

        try {
            const response = await axiosInstance.post('/employment/types', newType);
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Created',
                    detail: 'Employment type added successfully',
                    life: 3000
                });
                setNewTypeDialog(false);
                setNewType({ employment_type: '', description: '' });
                loadEmploymentTypes();
            }
        } catch (error) {
            console.error('Error creating employment type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create employment type',
                life: 3000
            });
        }
    };

    // ✅ Open edit dialog
    const openEditDialog = (type) => {
        setSelectedType({ ...type });
        setEditTypeDialog(true);
    };

    // ✅ Update employment type
    const updateEmploymentType = async () => {
        try {
            const response = await axiosInstance.put(`/employment/types/${selectedType.id}`, selectedType);
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Employment type updated successfully',
                    life: 3000
                });
                setEditTypeDialog(false);
                loadEmploymentTypes();
            }
        } catch (error) {
            console.error('Error updating employment type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update employment type',
                life: 3000
            });
        }
    };

    // ✅ Delete employment type
    const deleteEmploymentType = async (id) => {
        if (!window.confirm('Are you sure you want to delete this type?')) return;

        try {
            const response = await axiosInstance.delete(`/employment/types/${id}`);
            if (response.data.success) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Deleted',
                    detail: 'Employment type deleted successfully',
                    life: 3000
                });
                loadEmploymentTypes();
            }
        } catch (error) {
            console.error('Error deleting employment type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete employment type',
                life: 3000
            });
        }
    };

    // ✅ Action buttons
    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-info" onClick={() => openEditDialog(rowData)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => deleteEmploymentType(rowData.id)} />
        </div>
    );

    // ✅ Dialog footers
    const createFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setNewTypeDialog(false)} />
            <Button label="Create" icon="pi pi-check" className="p-button-text" onClick={createEmploymentType} />
        </div>
    );

    const editFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setEditTypeDialog(false)} />
            <Button label="Update" icon="pi pi-check" className="p-button-text" onClick={updateEmploymentType} />
        </div>
    );

    return (
        <div className="p-4">
            <Toast ref={toast} />

            <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-semibold">Employment Types</h2>
                <Button
                    label="New Employment Type"
                    icon="pi pi-plus"
                    className="p-button-success"
                    onClick={() => setNewTypeDialog(true)}
                />
            </div>

            <DataTable
                value={employmentTypes}
                loading={loading}
                paginator
                rows={10}
                dataKey="id"
                responsiveLayout="scroll"
                emptyMessage="No employment types found"
            >
                <Column field="id" header="ID" sortable style={{ width: '10%' }} />
                <Column field="employment_type" header="Employment Type" sortable style={{ width: '40%' }} />
                <Column field="description" header="Description" style={{ width: '40%' }} />
                <Column header="Actions" body={actionBodyTemplate} style={{ width: '10%' }} />
            </DataTable>

            {/* Create New Type Dialog */}
            <Dialog
                visible={newTypeDialog}
                style={{ width: '500px' }}
                header="Create New Employment Type"
                modal
                className="p-fluid"
                footer={createFooter}
                onHide={() => setNewTypeDialog(false)}
            >
                <div className="field">
                    <label htmlFor="employment_type">Employment Type</label>
                    <InputText
                        id="employment_type"
                        value={newType.employment_type}
                        onChange={(e) => setNewType({ ...newType, employment_type: e.target.value })}
                    />
                </div>
                <div className="field">
                    <label htmlFor="description">Description</label>
                    <InputText
                        id="description"
                        value={newType.description}
                        onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                    />
                </div>
            </Dialog>

            {/* Edit Type Dialog */}
            <Dialog
                visible={editTypeDialog}
                style={{ width: '500px' }}
                header="Edit Employment Type"
                modal
                className="p-fluid"
                footer={editFooter}
                onHide={() => setEditTypeDialog(false)}
            >
                {selectedType && (
                    <>
                        <div className="field">
                            <label htmlFor="employment_type">Employment Type</label>
                            <InputText
                                id="employment_type"
                                value={selectedType.employment_type}
                                onChange={(e) =>
                                    setSelectedType({ ...selectedType, employment_type: e.target.value })
                                }
                            />
                        </div>
                        <div className="field">
                            <label htmlFor="description">Description</label>
                            <InputText
                                id="description"
                                value={selectedType.description}
                                onChange={(e) =>
                                    setSelectedType({ ...selectedType, description: e.target.value })
                                }
                            />
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
}
